import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import { Badge } from '@/shared/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  ArrowRight,
  CircleDot,
  GitBranch,
  ListChecks,
  Loader2,
  MapPin,
  Paperclip,
  Plus,
  Users,
  X,
} from 'lucide-react';
import CustomCombobox from '@/shared/ui/custom/combobox';
import {
  AddNewItemTypeDialog,
  AddNewTradeDialog,
  AddNewCostCodeDialog,
} from '@/shared/forms';
import {
  usePunchListItemTypes,
  useTrades,
  useCostCodes,
  useCreateCostCode,
  usePunchListMutations,
  useProjectUsers,
  useCreatePunchListItemType,
  useCreateTrade,
  usePunchWorkflowMutations,
  usePunchWorkflowBatches,
  usePunchWorkflowRuns,
  usePunchItemWorkflowTemplates,
  useProjectAreaTags,
  useTradePartners,
} from '@/features/punch-lists/hooks';
import {
  type CreatePunchListItemData,
  type ImpactChoice,
  type PunchListPriority,
  type UserMinimal,
} from '@/features/punch-lists/types';
import { handleApiError } from '@/shared/api/requests';
import { ViewPosition } from '../types/types';
import { useCreateHotspot } from '../hooks/useHotspotData';
import { AttachmentPhotoGallery, type AttachmentPhoto } from '@/shared/components/AttachmentPhotoGallery';
import type { PendingPhotoUpload } from './pendingPhotoUpload';
import {
  PunchItemWorkflowDialog,
  type PunchWorkflowDialogSubmission,
} from '@/features/punch-lists/components/PunchItemWorkflowDialog';
import { workflowRunLocationLabel } from '@/features/punch-lists/utils/workflowLocation';

interface PunchItemHotspotFormProps {
  photoId: number;
  projectId: number | undefined;
  viewPosition: ViewPosition;
  hotspotId?: number;
  initialFiles?: File[];
  initialPreviewEntries?: Array<{
    id: string;
    file: File;
    previewUrl: string;
    isExternalPreview?: boolean;
  }>;
  /**
   * Photos that started uploading to GCS in the background (e.g. while the
   * user was filling out the form). On submit we await these and link them
   * to the new item by `photo_id` instead of re-uploading.
   */
  pendingUploads?: PendingPhotoUpload[];
  /** Called when the user removes one of the parent-owned `pendingUploads`. */
  onRemovePendingUpload?: (id: string) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

const IMPACT_OPTIONS: { value: ImpactChoice; label: string }[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'YES_UNKNOWN', label: 'Yes (Unknown)' },
  { value: 'NO', label: 'No' },
  { value: 'TBD', label: 'TBD' },
  { value: 'NA', label: 'N/A' },
];

const PRIORITY_OPTIONS: { value: PunchListPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

type FormState = Omit<CreatePunchListItemData, 'project' | 'hotspot'>;
type IssueKind = 'single' | 'multipart' | null;
type SavedProcessStartMode = 'separate' | 'batch' | 'new-batch';

const INITIAL_FORM: FormState = {
  title: '',
  priority: 'MEDIUM',
  status: 'OPEN',
  description: '',
  cost_impact: 'NA',
  schedule_impact: 'NA',
  is_private: false,
  due_date: '',
  punch_manager: null,
  assignee: null,
  final_approver: null,
  item_type: null,
  trade: null,
  cost_code: null,
  location: null,
  distribution_list: [],
};

const userDisplayName = (u: UserMinimal) =>
  `${u.first_name} ${u.last_name}`.trim() || u.email;

const tradePartnerCount = (
  steps: Array<{ responsible_trade_partner: number | null }>,
) => new Set(
  steps
    .map((step) => step.responsible_trade_partner)
    .filter((partnerId): partnerId is number => partnerId != null && partnerId > 0),
).size;

const tradePartnerCountLabel = (count: number) =>
  `${count} Trade ${count === 1 ? 'Partner' : 'Partners'}`;

const taskCountLabel = (count: number) => `${count} ${count === 1 ? 'task' : 'tasks'}`;

export const PunchItemHotspotForm: React.FC<PunchItemHotspotFormProps> = ({
  photoId,
  projectId,
  viewPosition,
  hotspotId: existingHotspotId,
  initialFiles = [],
  initialPreviewEntries = [],
  pendingUploads = [],
  onRemovePendingUpload,
  onSuccess,
  onCancel,
}) => {
  const { createItem, uploadAttachment, linkAttachmentPhoto } = usePunchListMutations(projectId);
  const { data: itemTypes } = usePunchListItemTypes();
  const { data: trades } = useTrades();
  const { data: costCodes } = useCostCodes();
  const { data: projectUsers } = useProjectUsers(projectId);
  const { data: tradePartners = [] } = useTradePartners(projectId);
  const createItemType = useCreatePunchListItemType();
  const createTrade = useCreateTrade();
  const createCostCode = useCreateCostCode();
  const { data: workflowTemplates = [], isLoading: workflowsLoading } =
    usePunchItemWorkflowTemplates(projectId);
  const { data: workflowRuns = [], isLoading: workflowRunsLoading } =
    usePunchWorkflowRuns(projectId);
  const { data: workflowBatches = [], isLoading: workflowBatchesLoading } =
    usePunchWorkflowBatches(projectId);
  const { data: workflowAreaTags = [] } = useProjectAreaTags(projectId);
  const { launchRun, launchBatch, addRunToBatch } = usePunchWorkflowMutations(projectId);
  const createHotspotMutation = useCreateHotspot(
    projectId == null ? undefined : String(projectId),
  );

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [issueKind, setIssueKind] = useState<IssueKind>(null);
  const [pendingFiles, setPendingFiles] = useState<Array<{ id: string; file: File; previewUrl: string; isExternalPreview?: boolean }>>(() => {
    // When the parent (FABActionDialog) owns photos via pendingUploads,
    // the form should NOT also re-upload them as files.
    if (pendingUploads.length > 0) return [];
    if (initialPreviewEntries.length > 0) {
      return initialPreviewEntries.map((entry) => ({ ...entry, isExternalPreview: true }));
    }
    return initialFiles.map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
      file,
      previewUrl: URL.createObjectURL(file),
      isExternalPreview: false,
    }));
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowWizardOpen, setWorkflowWizardOpen] = useState(false);
  const [workflowTemplateId, setWorkflowTemplateId] = useState('');
  const [savedProcessStartMode, setSavedProcessStartMode] =
    useState<SavedProcessStartMode>('separate');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [workflowSubmitting, setWorkflowSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<Array<{ id: string; file: File; previewUrl: string; isExternalPreview?: boolean }>>([]);

  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setNullableUser = (key: 'punch_manager' | 'assignee' | 'final_approver', raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === 'none' ? null : Number(raw) }));

  const toggleDistribution = (userId: number) => {
    setForm((prev) => {
      const current = prev.distribution_list as number[];
      return {
        ...prev,
        distribution_list: current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      };
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) {
      setPendingFiles((prev) => [
        ...prev,
        ...files.map((file, index) => ({
          id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
          file,
          previewUrl: URL.createObjectURL(file),
          isExternalPreview: false,
        })),
      ]);
    }
    e.target.value = '';
  };

  useEffect(() => () => {
    pendingFilesRef.current.forEach((entry) => {
      if (!entry.isExternalPreview) URL.revokeObjectURL(entry.previewUrl);
    });
  }, []);

  const attachmentPreviews = useMemo<AttachmentPhoto[]>(
    () => [
      ...pendingUploads.map((entry) => ({
        id: entry.id,
        file: entry.file.name,
        file_url: entry.previewUrl,
      })),
      ...pendingFiles.map((entry) => ({
        id: entry.id,
        file: entry.file.name,
        file_url: entry.previewUrl,
      })),
    ],
    [pendingFiles, pendingUploads],
  );

  const removeFileById = (attachmentId: string) => {
    if (pendingUploads.some((entry) => entry.id === attachmentId)) {
      onRemovePendingUpload?.(attachmentId);
      return;
    }
    setPendingFiles((prev) => {
      const target = prev.find((entry) => entry.id === attachmentId);
      if (target && !target.isExternalPreview) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((entry) => entry.id !== attachmentId);
    });
  };

  const handleAddNewType = async (name: string) => {
    const newType = await createItemType.mutateAsync(name);
    set('item_type', newType.id);
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    set('trade', newTrade.id);
  };

  const handleAddNewCostCode = async (value: string) => {
    const parsed = JSON.parse(value) as { code: string; name: string };
    const newCostCode = await createCostCode.mutateAsync(parsed);
    set('cost_code', newCostCode.id);
  };

  const getOrCreateHotspot = async () => {
    if (existingHotspotId) return existingHotspotId;
    const hotspotResponse = await createHotspotMutation.mutateAsync({
      photoId,
      position: {
        pitch: viewPosition.pitch,
        yaw: viewPosition.yaw,
        hfov: viewPosition.hfov,
      },
    });
    return hotspotResponse.id;
  };

  const handleStartInlineWorkflow = async (data: PunchWorkflowDialogSubmission) => {
    if (data.mode !== 'start') return;
    if (!projectId) throw new Error('No project context available.');

    const hotspotId = await getOrCreateHotspot();
    const result = await launchRun.mutateAsync({
      workflow: null,
      hotspot: hotspotId,
      name: data.definition.name,
      description: data.definition.description,
      is_template: data.definition.is_template,
      steps: data.definition.steps,
      punch_manager: data.punchManager,
      final_approver: data.finalApprover,
      is_private: data.isPrivate,
    });
    await onSuccess();
    return result;
  };

  const handleStartWorkflow = async () => {
    if (!projectId) {
      setWorkflowError('No project context available.');
      return;
    }
    if (!selectedWorkflowTemplateId) {
      setWorkflowError('Select a workflow.');
      return;
    }
    if (savedProcessStartMode === 'batch' && !selectedBatchId) {
      setWorkflowError('Choose the batch this issue should join.');
      return;
    }

    setWorkflowError(null);
    setWorkflowSubmitting(true);
    try {
      const hotspotId = await getOrCreateHotspot();

      if (savedProcessStartMode === 'batch') {
        await addRunToBatch.mutateAsync({
          batchId: selectedBatchId,
          data: { hotspot: hotspotId },
        });
      } else if (savedProcessStartMode === 'new-batch') {
        await launchBatch.mutateAsync({
          workflow: selectedWorkflowTemplateId,
          hotspot: hotspotId,
        });
      } else {
        await launchRun.mutateAsync({
          workflow: selectedWorkflowTemplateId,
          hotspot: hotspotId,
        });
      }
      setWorkflowDialogOpen(false);
      await onSuccess();
    } catch (workflowLaunchError) {
      setWorkflowError(handleApiError(workflowLaunchError));
    } finally {
      setWorkflowSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!projectId) {
      setError('No project context available.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const hotspotId = await getOrCreateHotspot();

      const payload: CreatePunchListItemData = {
        ...form,
        project: projectId,
        hotspot: hotspotId,
        due_date: form.due_date || null,
        punch_manager: form.punch_manager || null,
        assignee: form.assignee || null,
        final_approver: form.final_approver || null,
        item_type: form.item_type || null,
        trade: form.trade || null,
        cost_code: form.cost_code || null,
        location: form.location || null,
      };

      const newItem = await createItem.mutateAsync(payload);

      // Wait for any in-flight background uploads from the dialog, then
      // link the resulting Photo objects to the new item by id (parallel,
      // no re-upload). Anything that failed in the background is retried
      // here via the regular file upload path.
      const settled = await Promise.allSettled(
        pendingUploads.map((entry) => entry.uploadPromise),
      );
      const linkOps: Promise<unknown>[] = [];
      settled.forEach((result, idx) => {
        const entry = pendingUploads[idx];
        if (result.status === 'fulfilled') {
          linkOps.push(
            linkAttachmentPhoto.mutateAsync({
              itemId: newItem.id,
              photoId: result.value.photoId,
            }),
          );
        } else {
          linkOps.push(
            uploadAttachment.mutateAsync({ itemId: newItem.id, file: entry.file }),
          );
        }
      });
      pendingFiles.forEach((attachment) => {
        linkOps.push(
          uploadAttachment.mutateAsync({ itemId: newItem.id, file: attachment.file }),
        );
      });
      await Promise.all(linkOps);

      setForm(INITIAL_FORM);
      pendingFiles.forEach((entry) => {
        if (!entry.isExternalPreview) URL.revokeObjectURL(entry.previewUrl);
      });
      setPendingFiles([]);
      onSuccess();
    } catch {
      setError('Failed to create punch item. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const distList = (form.distribution_list as number[]) ?? [];
  const selectedWorkflowTemplateId = workflowTemplates.some(
    (template) => template.id === workflowTemplateId,
  )
    ? workflowTemplateId
    : (workflowTemplates[0]?.id ?? '');
  const selectedWorkflowTemplate = workflowTemplates.find(
    (template) => template.id === selectedWorkflowTemplateId,
  );
  const selectedWorkflowStages = useMemo(() => {
    if (!selectedWorkflowTemplate) return [];
    const grouped = new Map<number, typeof selectedWorkflowTemplate.steps>();
    selectedWorkflowTemplate.steps.forEach((step) => {
      grouped.set(step.stage, [...(grouped.get(step.stage) ?? []), step]);
    });
    return [...grouped.entries()]
      .sort(([left], [right]) => left - right)
      .map(([stage, steps]) => ({
        stage,
        steps: [...steps].sort((left, right) => left.position - right.position),
      }));
  }, [selectedWorkflowTemplate]);
  const stageTradeLabel = (
    steps: NonNullable<typeof selectedWorkflowTemplate>['steps'],
  ) => {
    const tradeNames = [...new Set(
      steps.map((step) => step.trade_name?.trim()).filter((name): name is string => Boolean(name)),
    )];
    return tradeNames.length > 0 ? tradeNames.join('/') : 'Trade not assigned';
  };
  const openRunsForSelectedProcess = useMemo(
    () => workflowRuns.filter(
      (run) => run.workflow === selectedWorkflowTemplateId && run.status === 'ACTIVE',
    ),
    [selectedWorkflowTemplateId, workflowRuns],
  );
  const activeBatchesForSelectedProcess = useMemo(
    () => workflowBatches.filter(
      (batch) => batch.workflow === selectedWorkflowTemplateId && batch.status === 'ACTIVE',
    ),
    [selectedWorkflowTemplateId, workflowBatches],
  );
  const joinableBatches = useMemo(
    () => activeBatchesForSelectedProcess.filter((batch) => {
      const stages = batch.runs[0]?.definition.map((step) => step.stage) ?? [];
      return stages.length > 0 && batch.current_stage === Math.min(...stages);
    }),
    [activeBatchesForSelectedProcess],
  );
  const batchById = useMemo(
    () => new Map(workflowBatches.map((batch) => [batch.id, batch])),
    [workflowBatches],
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-1">
      {issueKind === null && (
        <section className="space-y-5 py-2">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Choose what to create</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-[-0.02em]">How many punch items do you need?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Create one punch item for a single issue, or create multiple punch items for work that needs to be coordinated.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setIssueKind('single')}
              className="group flex min-h-40 flex-col items-start rounded-[1.375rem] border border-border bg-card p-5 text-left transition-colors hover:border-primary/50 hover:bg-muted/50"
            >
              <span className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground transition-colors group-hover:text-foreground">
                <CircleDot className="size-5" />
              </span>
              <span className="mt-5 font-display text-lg font-bold">One punch item</span>
              <span className="mt-1 text-sm leading-6 text-muted-foreground">
                Create a single punch item for one clear issue.
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIssueKind('multipart');
              }}
              disabled={!projectId}
              className="group flex min-h-40 flex-col items-start rounded-[1.375rem] border border-border bg-card p-5 text-left transition-colors hover:border-primary/50 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground transition-colors group-hover:text-foreground">
                <ListChecks className="size-5" />
              </span>
              <span className="mt-5 font-display text-lg font-bold">Multiple punch items</span>
              <span className="mt-1 text-sm leading-6 text-muted-foreground">
                Create several punch items and coordinate what can happen together or what needs to happen next.
              </span>
            </button>
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </section>
      )}

      {issueKind === 'multipart' && !workflowWizardOpen && !workflowDialogOpen && (
        <section className="space-y-5 py-2">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Choose a workflow</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-[-0.02em]">
              Is this a new workflow or one you&apos;ve used before?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Build a new workflow, or start from one of your workflow templates.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setWorkflowWizardOpen(true)}
            className="group flex w-full items-center gap-4 rounded-[1.375rem] border border-primary/40 bg-primary/5 p-5 text-left transition-colors hover:bg-primary/10"
          >
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Plus className="size-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-lg font-bold">Create a new workflow</span>
              <span className="mt-1 block text-sm text-muted-foreground">Lay out the tasks and decide which work can happen together.</span>
            </span>
            <ArrowRight className="size-5 shrink-0 text-primary" />
          </button>

          <div className="space-y-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Workflow templates</p>
              <p className="mt-1 text-sm text-muted-foreground">Choose one to start it at this hotspot.</p>
            </div>
            {workflowsLoading ? (
              <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/35 p-4 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading workflow templates…
              </div>
            ) : workflowTemplates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-muted/35 p-4 text-sm text-muted-foreground">
                No workflow templates yet. Create a new workflow above.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {workflowTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => {
                      setWorkflowTemplateId(template.id);
                      setWorkflowError(null);
                      setSavedProcessStartMode('separate');
                      setSelectedBatchId('');
                      setWorkflowDialogOpen(true);
                    }}
                    className="group rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/50"
                  >
                    <span className="block font-display font-bold">{template.name}</span>
                    {template.description && (
                      <span className="mt-1 line-clamp-2 block text-xs leading-5 text-muted-foreground">{template.description}</span>
                    )}
                    <span className="mt-3 block text-xs font-semibold text-muted-foreground">
                      {tradePartnerCountLabel(tradePartnerCount(template.steps))} · {taskCountLabel(template.steps.length)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => setIssueKind(null)}>Back</Button>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </section>
      )}

      {issueKind === 'multipart' && projectId && (
        <PunchItemWorkflowDialog
          open={workflowWizardOpen}
          onOpenChange={(open) => {
            setWorkflowWizardOpen(open);
          }}
          projectId={projectId}
          heading="Create a workflow for this issue"
          trades={trades ?? []}
          tradePartners={tradePartners}
          context="hotspot"
          onSubmit={handleStartInlineWorkflow}
        />
      )}

      {issueKind === 'single' && (
        <>

      {/* Title */}
      <div className="space-y-1">
        <Label htmlFor="pi-title">
          Title <span className="text-destructive">*</span>
        </Label>
        <Input
          id="pi-title"
          placeholder="Describe the punch item"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          autoFocus
        />
      </div>

      {/* Priority + Type */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Priority</Label>
          <Select
            value={form.priority}
            onValueChange={(v) => set('priority', v as PunchListPriority)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Type</Label>
          <CustomCombobox
            options={(itemTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
            value={form.item_type ?? ''}
            onChange={(v) => set('item_type', v || null)}
            placeholder="Select type"
            searchPlaceholder="Search types…"
            addNewLabel="Add New Type"
            emptyMessage="No types found."
            onAddNew={handleAddNewType}
            AddNewDialog={AddNewItemTypeDialog}
          />
        </div>
      </div>

      {/* Assignee + Final Approver */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Assignee</Label>
          <Select
            value={form.assignee != null ? String(form.assignee) : 'none'}
            onValueChange={(v) => setNullableUser('assignee', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {(projectUsers ?? []).map((u) => (
                <SelectItem key={u.id} value={String(u.id)}>
                  {userDisplayName(u)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Final Approver</Label>
          <Select
            value={form.final_approver != null ? String(form.final_approver) : 'none'}
            onValueChange={(v) => setNullableUser('final_approver', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select final approver" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {(projectUsers ?? []).map((u) => (
                <SelectItem key={u.id} value={String(u.id)}>
                  {userDisplayName(u)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Due Date */}
      <div className="space-y-1">
        <Label htmlFor="pi-due-date">Due Date</Label>
        <Input
          id="pi-due-date"
          type="datetime-local"
          value={form.due_date ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            if (!val) {
              set('due_date', val);
              return;
            }
            const prevDate = (form.due_date ?? '').slice(0, 10);
            const newDate = val.slice(0, 10);
            const newTime = val.slice(11);
            const prevTime = (form.due_date ?? '').slice(11);
            if (newDate !== prevDate && newTime === prevTime) {
              set('due_date', `${newDate}T23:59`);
            } else if (!form.due_date) {
              set('due_date', `${newDate}T23:59`);
            } else {
              set('due_date', val);
            }
          }}
        />
      </div>

      {/* Trade + Cost Code */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Trade</Label>
          <CustomCombobox
            options={(trades ?? []).map((t) => ({ value: t.id, label: t.name }))}
            value={form.trade ?? ''}
            onChange={(v) => set('trade', v || null)}
            placeholder="Select trade"
            searchPlaceholder="Search trades…"
            addNewLabel="Add New Trade"
            emptyMessage="No trades found."
            onAddNew={handleAddNewTrade}
            AddNewDialog={AddNewTradeDialog}
          />
        </div>

        <div className="space-y-1">
          <Label>Cost Code</Label>
          <CustomCombobox
            options={(costCodes ?? []).map((c) => ({ value: c.id, label: `${c.code} – ${c.name}` }))}
            value={form.cost_code ?? ''}
            onChange={(v) => set('cost_code', v || null)}
            placeholder="Select cost code"
            searchPlaceholder="Search cost codes…"
            addNewLabel="Add New Cost Code"
            emptyMessage="No cost codes found."
            onAddNew={handleAddNewCostCode}
            AddNewDialog={AddNewCostCodeDialog}
          />
        </div>
      </div>

      {/* Cost + Schedule Impact */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Cost Impact</Label>
          <Select
            value={form.cost_impact}
            onValueChange={(v) => set('cost_impact', v as ImpactChoice)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMPACT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Schedule Impact</Label>
          <Select
            value={form.schedule_impact}
            onValueChange={(v) => set('schedule_impact', v as ImpactChoice)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMPACT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Distribution List */}
      <div className="space-y-2">
        <Label>Distribution List</Label>
        <div className="border rounded-md p-3 max-h-32 overflow-y-auto space-y-1">
          {(projectUsers ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No users available</p>
          ) : (
            (projectUsers ?? []).map((u) => (
              <div key={u.id} className="flex items-center gap-2">
                <Checkbox
                  id={`pi-dist-${u.id}`}
                  checked={distList.includes(u.id)}
                  onCheckedChange={() => toggleDistribution(u.id)}
                />
                <label htmlFor={`pi-dist-${u.id}`} className="text-sm cursor-pointer">
                  {userDisplayName(u)}
                </label>
              </div>
            ))
          )}
        </div>
        {distList.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {distList.map((id) => {
              const u = (projectUsers ?? []).find((u) => u.id === id);
              return u ? (
                <Badge key={id} variant="secondary" className="gap-1">
                  {userDisplayName(u)}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => toggleDistribution(id)}
                  />
                </Badge>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Description */}
      <div className="space-y-1">
        <Label htmlFor="pi-description">Description</Label>
        <Textarea
          id="pi-description"
          placeholder="Describe the issue and possible resolution"
          rows={3}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>

      {/* Attachments */}
      <div className="space-y-2">
        <Label>Attachments</Label>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" />
            Attach Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
        {attachmentPreviews.length > 0 && (
          <AttachmentPhotoGallery attachments={attachmentPreviews} maxVisible={6} onDelete={removeFileById} />
        )}
      </div>

      {/* Private */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="pi-private"
          checked={form.is_private}
          onCheckedChange={(checked) => set('is_private', checked === true)}
        />
        <Label htmlFor="pi-private" className="cursor-pointer">
          Private — only visible to creator, assignee, and distribution list
        </Label>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => setIssueKind(null)}>
          Back
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Punch Item
        </Button>
      </div>
        </>
      )}

      <Dialog
        open={workflowDialogOpen}
        onOpenChange={(open) => {
          setWorkflowDialogOpen(open);
        }}
      >
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden p-0">
          <DialogHeader>
            <div className="px-6 pt-6">
              <DialogTitle>Review this workflow template</DialogTitle>
              <DialogDescription className="mt-1">
                See exactly what will happen, who owns each task, and whether this issue should move with other open work.
              </DialogDescription>
            </div>
          </DialogHeader>

          {workflowsLoading ? (
            <div className="flex items-center gap-2 px-6 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading workflow template…
            </div>
          ) : workflowTemplates.length === 0 ? (
            <div className="mx-6 rounded-[13px] border border-dashed border-border bg-muted/50 px-4 py-6 text-center text-sm text-muted-foreground">
              This workflow template is no longer available.
            </div>
          ) : selectedWorkflowTemplate ? (
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 pb-2">
              <section className="rounded-2xl border border-border bg-muted/35 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display text-xl font-extrabold tracking-[-0.02em]">
                      {selectedWorkflowTemplate.name}
                    </h3>
                    {selectedWorkflowTemplate.description && (
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                        {selectedWorkflowTemplate.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{taskCountLabel(selectedWorkflowTemplate.steps.length)}</Badge>
                    <Badge variant="secondary">
                      {selectedWorkflowStages.length} {selectedWorkflowStages.length === 1 ? 'work group' : 'work groups'}
                    </Badge>
                    <Badge variant="secondary">
                      {tradePartnerCountLabel(tradePartnerCount(selectedWorkflowTemplate.steps))}
                    </Badge>
                  </div>
                </div>
              </section>

              <section className="space-y-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">The work</p>
                  <h3 className="mt-1 font-display text-lg font-bold">Tasks and handoffs</h3>
                </div>
                <div className="space-y-3">
                  {selectedWorkflowStages.map(({ stage, steps }, stageIndex) => (
                    <div key={stage} className="rounded-2xl border border-border bg-card p-4">
                      <div className="flex items-center gap-3 border-b border-border pb-3">
                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                          {stageIndex + 1}
                        </span>
                        <p className="font-display font-bold">{stageTradeLabel(steps)}</p>
                      </div>
                      <div className="mt-3 divide-y divide-border">
                        {steps.map((step) => {
                          const assignment = step.responsible_trade_partner_name
                            ? `${step.responsible_trade_partner_name}${step.trade_name ? ` · ${step.trade_name}` : ''}`
                            : step.trade_name
                              ? `${step.trade_name} · Trade partner not assigned`
                              : 'Trade and trade partner not assigned';
                          return (
                            <div key={step.id} className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.65fr)]">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold">{step.title}</p>
                                {step.description && (
                                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{step.description}</p>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground sm:text-right">
                                <p className="font-medium text-foreground">{assignment}</p>
                                <p className="mt-1">
                                  {step.priority.charAt(0) + step.priority.slice(1).toLowerCase()} priority
                                  {step.due_days_after_activation > 0
                                    ? ` · due ${step.due_days_after_activation} ${step.due_days_after_activation === 1 ? 'day' : 'days'} after it starts`
                                    : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Open work</p>
                    <h3 className="mt-1 font-display text-lg font-bold">Is this workflow already underway?</h3>
                  </div>
                  {!workflowRunsLoading && (
                    <Badge variant={openRunsForSelectedProcess.length > 0 ? 'default' : 'secondary'}>
                      {openRunsForSelectedProcess.length} open
                    </Badge>
                  )}
                </div>
                {workflowRunsLoading || workflowBatchesLoading ? (
                  <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/35 p-4 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" /> Checking open work…
                  </div>
                ) : openRunsForSelectedProcess.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border bg-muted/35 p-4 text-sm text-muted-foreground">
                    No other issues are currently using this workflow.
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {openRunsForSelectedProcess.map((run) => {
                      const runBatch = run.batch ? batchById.get(run.batch) : undefined;
                      const locationLabel = workflowRunLocationLabel(
                        run,
                        workflowAreaTags,
                        existingHotspotId,
                      );
                      return (
                        <div key={run.id} className="rounded-xl border border-border bg-card p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="flex items-center gap-1.5 text-sm font-semibold">
                                <MapPin className="size-3.5 shrink-0 text-primary" />
                                <span className="truncate">{locationLabel}</span>
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Work group {run.current_stage} · {run.completed_steps} of {run.total_steps} tasks finished
                              </p>
                            </div>
                            {runBatch && <Badge variant="outline">In a batch</Badge>}
                          </div>
                          {runBatch && (
                            <p className="mt-2 truncate text-xs font-medium text-muted-foreground">{runBatch.name}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">How it should move</p>
                  <h3 className="mt-1 font-display text-lg font-bold">Should this issue join a batch?</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    A batch keeps matching work together so the next work group waits until the current group is finished for every issue.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSavedProcessStartMode('separate');
                      setWorkflowError(null);
                    }}
                    className={`rounded-xl border p-4 text-left transition-colors ${savedProcessStartMode === 'separate' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/50'}`}
                  >
                    <span className="flex items-center gap-2 font-display font-bold">
                      <GitBranch className="size-4" /> Start it separately
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      This issue moves forward as soon as its own tasks are finished.
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (joinableBatches.length > 0) {
                        setSavedProcessStartMode('batch');
                        setSelectedBatchId((current) => current || joinableBatches[0].id);
                      } else {
                        setSavedProcessStartMode('new-batch');
                        setSelectedBatchId('');
                      }
                      setWorkflowError(null);
                    }}
                    className={`rounded-xl border p-4 text-left transition-colors ${savedProcessStartMode === 'batch' || savedProcessStartMode === 'new-batch' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/50'}`}
                  >
                    <span className="flex items-center gap-2 font-display font-bold">
                      <Users className="size-4" />
                      {joinableBatches.length > 0 ? 'Add it to a batch' : 'Start Batch'}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {joinableBatches.length > 0
                        ? `Choose from ${joinableBatches.length} open ${joinableBatches.length === 1 ? 'batch' : 'batches'} still in the first work group.`
                        : 'Start this issue as a batch so matching issues can be added as you find them.'}
                    </span>
                  </button>
                </div>

                {savedProcessStartMode === 'batch' && joinableBatches.length > 0 && (
                  <div className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-4">
                    <Label>Which batch should this issue join?</Label>
                    <Select value={selectedBatchId} onValueChange={setSelectedBatchId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose an open batch" />
                      </SelectTrigger>
                      <SelectContent>
                        {joinableBatches.map((batch) => (
                          <SelectItem key={batch.id} value={batch.id}>
                            {batch.name} · {batch.total_runs} {batch.total_runs === 1 ? 'issue' : 'issues'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs leading-5 text-muted-foreground">
                      This issue will start in the batch&apos;s current work group. The batch will wait for it before handing off the next group.
                    </p>
                  </div>
                )}

                {activeBatchesForSelectedProcess.length > joinableBatches.length && (
                  <p className="text-xs leading-5 text-muted-foreground">
                    {activeBatchesForSelectedProcess.length - joinableBatches.length}{' '}
                    {activeBatchesForSelectedProcess.length - joinableBatches.length === 1 ? 'other batch is' : 'other batches are'} already past the first handoff, so new issues can&apos;t join.
                  </p>
                )}
              </section>
            </div>
          ) : null}

          <div className="px-6">
            {workflowError && <p className="text-sm text-destructive">{workflowError}</p>}
          </div>

          <DialogFooter className="border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setWorkflowDialogOpen(false);
              }}
              disabled={workflowSubmitting}
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={() => void handleStartWorkflow()}
              disabled={
                !selectedWorkflowTemplateId
                || workflowsLoading
                || workflowSubmitting
                || (savedProcessStartMode === 'batch' && !selectedBatchId)
              }
            >
              {workflowSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {savedProcessStartMode === 'batch'
                ? 'Add to batch'
                : savedProcessStartMode === 'new-batch'
                  ? 'Start Batch'
                  : 'Start separately'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};
